import { useEffect, useMemo, useState } from 'react'
import { createClient } from 'urql'
import { gqlClientOptions } from 'gql/base'
import {
	CONSOLIDATOR_FEATURE_FLAG_QUERY,
	ConsolidatorFeatureFlagQueryResult
} from 'gql/consolidatorFeatureFlag'
import { RequestLevel } from '../constants'

interface UseConsolidatorFeatureFlagOptions {
	/** Skip the query (e.g. while practice context is still loading). */
	pause?: boolean
}

interface UseConsolidatorFeatureFlagResult {
	enabled: boolean
	loading: boolean
	error?: Error
}

/**
 * Generic hook that resolves a single consolidator feature flag via the
 * `consolidatorFeatureFlag(name: String!): Boolean!` query on `/admin/graphql`.
 *
 * The backend reads the consolidator off the request context, so callers don't
 * pass an id. Missing flags resolve to `false`, matching the backend default.
 *
 * @param name - feature flag key on `consolidator.featureFlags` (e.g. `enableRebatePayoutsPage`).
 * @param options - `pause` to defer the query until other context is ready.
 */
export const useConsolidatorFeatureFlag = (
	name: string,
	options: UseConsolidatorFeatureFlagOptions = {}
): UseConsolidatorFeatureFlagResult => {
	const { pause } = options

	const [enabled, setEnabled] = useState(false)
	const [loading, setLoading] = useState(!pause)
	const [error, setError] = useState<Error | undefined>()

	const client = useMemo(
		() => createClient(gqlClientOptions({ requestLevel: RequestLevel.ADMIN })),
		[]
	)

	useEffect(() => {
		if (pause) {
			return
		}

		let cancelled = false
		setLoading(true)
		setError(undefined)

		client
			.query<ConsolidatorFeatureFlagQueryResult>(CONSOLIDATOR_FEATURE_FLAG_QUERY, { name })
			.toPromise()
			.then((result) => {
				if (cancelled) {
					return
				}
				if (result.error) {
					setError(result.error)
					setEnabled(false)
				} else {
					setEnabled(Boolean(result.data?.consolidatorFeatureFlag))
				}
			})
			.catch((err) => {
				if (cancelled) {
					return
				}
				setError(err as Error)
				setEnabled(false)
			})
			.finally(() => {
				if (!cancelled) {
					setLoading(false)
				}
			})

		return () => {
			cancelled = true
		}
	}, [client, name, pause])

	return { enabled, loading, error }
}
